import type { ApiResponse, PagedResponseDto } from "@/types/common";
import type {
  DeliveryDashboardStatsDto,
  DeliveryPartnerApprovalDto,
  DeliveryPartnerListDto,
  DeliveryPartnerRegisterDto,
  DeliveryPartnerResponseDto,
  DeliveryPartnerUpdateDto,
  LocationUpdateDto,
} from "@/types/delivery.dto";
import { apiClient, unwrapApiResponse } from "./httpClient";

export const deliveryPartnersService = {
  register(dto: DeliveryPartnerRegisterDto): Promise<DeliveryPartnerResponseDto> {
    return unwrapApiResponse(
      apiClient.post<ApiResponse<DeliveryPartnerResponseDto>>("/DeliveryPartners/register", dto),
    );
  },

  getMyProfile(): Promise<DeliveryPartnerResponseDto> {
    return unwrapApiResponse(
      apiClient.get<ApiResponse<DeliveryPartnerResponseDto>>("/DeliveryPartners/my-profile"),
    );
  },

  getById(id: number): Promise<DeliveryPartnerResponseDto> {
    return unwrapApiResponse(
      apiClient.get<ApiResponse<DeliveryPartnerResponseDto>>(`/DeliveryPartners/${id}`),
    );
  },

  getDashboard(id: number): Promise<DeliveryDashboardStatsDto> {
    return unwrapApiResponse(
      apiClient.get<ApiResponse<DeliveryDashboardStatsDto>>(`/DeliveryPartners/${id}/dashboard`),
    );
  },

  updateProfile(id: number, dto: DeliveryPartnerUpdateDto): Promise<DeliveryPartnerResponseDto> {
    return unwrapApiResponse(
      apiClient.put<ApiResponse<DeliveryPartnerResponseDto>>(`/DeliveryPartners/${id}`, dto),
    );
  },

  updateLocation(id: number, dto: LocationUpdateDto): Promise<boolean> {
    return unwrapApiResponse(
      apiClient.put<ApiResponse<boolean>>(`/DeliveryPartners/${id}/location`, dto),
    );
  },

  toggleOnline(id: number, isOnline: boolean): Promise<boolean> {
    return unwrapApiResponse(
      apiClient.put<ApiResponse<boolean>>(`/DeliveryPartners/${id}/online`, null, {
        params: { isOnline },
      }),
    );
  },

  toggleAvailability(id: number, isAvailable: boolean): Promise<boolean> {
    return unwrapApiResponse(
      apiClient.put<ApiResponse<boolean>>(`/DeliveryPartners/${id}/availability`, null, {
        params: { isAvailable },
      }),
    );
  },

  getAll(
    page = 1,
    pageSize = 20,
    status?: string,
    city?: string,
  ): Promise<PagedResponseDto<DeliveryPartnerListDto>> {
    return unwrapApiResponse(
      apiClient.get<ApiResponse<PagedResponseDto<DeliveryPartnerListDto>>>("/DeliveryPartners", {
        params: { page, pageSize, status, city },
      }),
    );
  },

  getPending(page = 1, pageSize = 20): Promise<PagedResponseDto<DeliveryPartnerListDto>> {
    return unwrapApiResponse(
      apiClient.get<ApiResponse<PagedResponseDto<DeliveryPartnerListDto>>>("/DeliveryPartners/pending", {
        params: { page, pageSize },
      }),
    );
  },

  approve(id: number, dto: DeliveryPartnerApprovalDto): Promise<boolean> {
    return unwrapApiResponse(
      apiClient.post<ApiResponse<boolean>>(`/DeliveryPartners/${id}/approve`, dto),
    );
  },

  suspend(id: number, reason: string): Promise<boolean> {
    return unwrapApiResponse(
      apiClient.post<ApiResponse<boolean>>(`/DeliveryPartners/${id}/suspend`, JSON.stringify(reason), {
        headers: { "Content-Type": "application/json" },
      }),
    );
  },
};
